import type { FC, ReactNode } from "react";
import { Suspense, useEffect, useRef, useState } from "react";

import type { BlockType } from "./min-heights";
import { MinHeightPlaceholder } from "./MinHeightPlaceholder";

type LazyBlockProps = {
  blockType: BlockType;
  isBot: boolean;
  children: ReactNode;
  forcedEager?: boolean;
};

const MOBILE_IO_ROOT_MARGIN = "200px 0px";
const DESKTOP_IO_ROOT_MARGIN = "800px 0px";
const MOBILE_MEDIA = "(max-width: 767.98px)";
const IO_THRESHOLD = 0;

export const LazyBlock: FC<LazyBlockProps> = ({
  blockType,
  isBot,
  children,
  forcedEager,
}) => {
  const [visible, setVisible] = useState<boolean>(
    isBot || forcedEager === true,
  );
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (visible) return;

    const node = ref.current;
    if (!node) return;

    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }

    const rootMargin = window.matchMedia(MOBILE_MEDIA).matches
      ? MOBILE_IO_ROOT_MARGIN
      : DESKTOP_IO_ROOT_MARGIN;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          setVisible(true);
          observer.disconnect();
          break;
        }
      },
      { rootMargin, threshold: IO_THRESHOLD },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [visible]);

  return (
    <div ref={ref}>
      <Suspense fallback={<MinHeightPlaceholder blockType={blockType} />}>
        {visible ? children : <MinHeightPlaceholder blockType={blockType} />}
      </Suspense>
    </div>
  );
};
